import React, { memo } from "react";
import { Button, Input, Tooltip, Typography } from "antd";
import {
  CalculatorOutlined,
  CloseOutlined,
  EditOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getParamMeta } from "../AlgorithmSettingsUtils";

const { Text } = Typography;

const AlgorithmSettingsCard = memo(function AlgorithmSettingsCard({
  parameterConfig,
  isEditing,
  hasSimulated,
  onToggleEdit,
  onImportanceChange,
  onResetToCurrent,
  onResetToDefault,
  onRunSimulation,
  simulationLoading,
}) {
  const rowClass = !isEditing
    ? "algorithm_grid_row--view"
    : hasSimulated
    ? "algorithm_grid_row--simulated"
    : "algorithm_grid_row--edit";

  const canSimulate =
    parameterConfig.length > 0 &&
    parameterConfig.every((param) => param.valueFrom !== "");

  return (
    <div className="box_wraper_div playground_scenario_card algorithm_settings_card">
      <div className="playground_card_header">
        <div className="playground_card_header_left">
          <Text className="playground_card_title">
            Ranking Algorithm Settings
          </Text>
          <Text className="playground_card_subtitle">
            Set the importance of each parameter to control how results are ranked.
          </Text>
        </div>
        <Button
          className={`playground_edit_toggle_btn ${
            isEditing ? "playground_edit_toggle_btn--cancel" : ""
          }`}
          icon={isEditing ? <CloseOutlined /> : <EditOutlined />}
          onClick={onToggleEdit}
          disabled={simulationLoading}
        >
          {isEditing ? "Cancel Edit" : "Edit"}
        </Button>
      </div>

      <div className="playground_scenario_table">
        <div
          className={`playground_scenario_head algorithm_grid_row ${rowClass}`}
        >
          <Text className="playground_col_head algorithm_col_head">Parameter</Text>
          <Text className="playground_col_head algorithm_col_head">Current Weight</Text>
          {isEditing && (
            <Text className="playground_col_head algorithm_col_head">
              Importance
            </Text>
          )}
          {isEditing && hasSimulated && (
            <Text className="playground_col_head algorithm_col_head">
              Updated Weight
            </Text>
          )}
        </div>

        {parameterConfig.map((param) => {
          const meta = getParamMeta(param.label, param.key);

          return (
            <div
              key={param.key}
              className={`algorithm_grid_row ${rowClass}`}
            >
              <div className="algorithm_param_info">
                <Text strong className="algorithm_param_title">
                  {meta.title}
                </Text>
                <Text type="secondary" className="algorithm_param_desc">
                  {meta.description}
                </Text>
              </div>

              <div className="playground_live_cell algorithm_live_cell">
                <Input
                  value={
                    param.valueTo !== undefined && param.valueTo !== null
                      ? String(param.valueTo)
                      : ""
                  }
                  title={String(param.valueTo ?? "")}
                  disabled
                  className="playground_live_input algorithm_live_input"
                />
              </div>

              {isEditing && (
                <div className="playground_whatif_cell algorithm_whatif_cell">
                  <div className="algorithm_importance_group">
                    {[1, 2, 3, 4, 5].map((num, i) => {
                      const selected = param.valueFrom === num;
                      const isFirst = i === 0;
                      const isLast = i === 4;
                      return (
                        <Button
                          key={num}
                          disabled={simulationLoading}
                          onClick={() =>
                            onImportanceChange(param.key, selected ? "" : num)
                          }
                          className={`algorithm_importance_btn ${
                            selected ? "algorithm_importance_btn--selected" : ""
                          }`}
                          style={{
                            borderTopLeftRadius: isFirst ? 6 : 0,
                            borderBottomLeftRadius: isFirst ? 6 : 0,
                            borderTopRightRadius: isLast ? 6 : 0,
                            borderBottomRightRadius: isLast ? 6 : 0,
                            marginLeft: isFirst ? 0 : -1,
                            borderColor: selected ? "#304cb2" : "#d9d9d9",
                            background: selected ? "#304cb2" : "#fff",
                            color: selected ? "#fff" : "#595959",
                          }}
                        >
                          {num}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              {isEditing && hasSimulated && (
                <div className="playground_live_cell algorithm_live_cell">
                  <Input
                    value={
                      param.updatedWeight !== undefined && param.updatedWeight !== null
                        ? String(param.updatedWeight)
                        : ""
                    }
                    title={String(param.updatedWeight ?? "")}
                    disabled
                    className="playground_live_input algorithm_live_input algorithm_updated_input"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isEditing ? (
        <div className="algorithm_scenario_actions_split">
          <Button
            onClick={onResetToDefault}
            disabled={parameterConfig.length === 0 || simulationLoading}
          >
            Reset to Defaults
          </Button>
          <div className="algorithm_scenario_actions_right">
            <Button
              icon={<ReloadOutlined />}
              onClick={onResetToCurrent}
              disabled={simulationLoading}
            >
              Reset to Current
            </Button>
            <Tooltip
              title={
                !canSimulate
                  ? "Select an importance value (1-5) for every parameter to run the simulation"
                  : ""
              }
            >
              <span>
                <Button
                  type="primary"
                  icon={<CalculatorOutlined />}
                  onClick={onRunSimulation}
                  disabled={!canSimulate}
                  loading={simulationLoading}
                  style={
                    canSimulate
                      ? { backgroundColor: "#304cb2", borderColor: "#304cb2" }
                      : undefined
                  }
                >
                  Simulate Ranking
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 14 }}>
          <Button
            onClick={onResetToDefault}
            disabled={parameterConfig.length === 0}
          >
            Reset to Defaults
          </Button>
        </div>
      )}
    </div>
  );
});

export default AlgorithmSettingsCard;
